import React, { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Slider,
    Alert,
    CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useUserAppsContext } from './user-apps-context';

const settingsValidationSchema = Yup.object({
    navigation_mode: Yup.string().oneOf(['hover', 'xy_cursor']).required(),
    dwell_time: Yup.number().min(1000).max(5000).required(),
    cursor_speed: Yup.number().min(1).max(5).required(),
});

/**
 * Navigation Settings Component
 * Allows users to configure navigation mode (hover/xy_cursor) and related settings
 *
 * @param {Object} props
 * @param {function} props.onSaveSuccess - Optional callback when settings are saved successfully
 */
export default function NavigationSettings({ onSaveSuccess }) {
    const { t } = useTranslation();
    const { userSettings, updateSettings, loadingSettings } = useUserAppsContext();
    const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null

    const handleSettingsSave = async (values, { setSubmitting }) => {
        setSaveStatus(null);
        const result = await updateSettings(values);
        setSubmitting(false);

        if (result.success) {
            setSaveStatus('success');
            setTimeout(() => setSaveStatus(null), 3000);

            // Call optional success callback
            if (onSaveSuccess) {
                onSaveSuccess();
            }
        } else {
            setSaveStatus('error');
        }
    };

    if (loadingSettings) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Formik
                initialValues={userSettings}
                validationSchema={settingsValidationSchema}
                onSubmit={handleSettingsSave}
                enableReinitialize
            >
                {({ values, handleChange, isSubmitting, setFieldValue }) => (
                    <Form>
                        {saveStatus === 'success' && (
                            <Alert severity="success" sx={{ mb: 3 }}>
                                {t('PROFILE.SETTINGS_SAVED')}
                            </Alert>
                        )}
                        {saveStatus === 'error' && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {t('PROFILE.SETTINGS_ERROR')}
                            </Alert>
                        )}

                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel id="navigation-mode-label">
                                {t('PROFILE.NAVIGATION_MODE')}
                            </InputLabel>
                            <Select
                                labelId="navigation-mode-label"
                                name="navigation_mode"
                                value={values.navigation_mode}
                                onChange={handleChange}
                                label={t('PROFILE.NAVIGATION_MODE')}
                            >
                                <MenuItem value="hover">
                                    {t('PROFILE.NAVIGATION_MODE_HOVER')}
                                </MenuItem>
                                <MenuItem value="xy_cursor">
                                    {t('PROFILE.NAVIGATION_MODE_XY')}
                                </MenuItem>
                            </Select>
                        </FormControl>

                        {values.navigation_mode === 'hover' && (
                            <Box sx={{ mb: 3 }}>
                                <Typography gutterBottom>
                                    {t('PROFILE.DWELL_TIME')}
                                </Typography>
                                <Slider
                                    name="dwell_time"
                                    value={values.dwell_time}
                                    onChange={(e, value) => setFieldValue('dwell_time', value)}
                                    min={1000}
                                    max={5000}
                                    step={100}
                                    marks={[
                                        { value: 1000, label: '1s' },
                                        { value: 2000, label: '2s' },
                                        { value: 3000, label: '3s' },
                                        { value: 4000, label: '4s' },
                                        { value: 5000, label: '5s' },
                                    ]}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(value) => t('PROFILE.DWELL_TIME_LABEL', { value })}
                                />
                            </Box>
                        )}

                        {values.navigation_mode === 'xy_cursor' && (
                            <Box sx={{ mb: 3 }}>
                                <Typography gutterBottom>
                                    {t('PROFILE.CURSOR_SPEED')}
                                </Typography>
                                <Slider
                                    name="cursor_speed"
                                    value={values.cursor_speed}
                                    onChange={(e, value) => setFieldValue('cursor_speed', value)}
                                    min={1}
                                    max={5}
                                    step={0.5}
                                    marks={[
                                        { value: 1, label: '1' },
                                        { value: 2, label: '2' },
                                        { value: 3, label: '3' },
                                        { value: 4, label: '4' },
                                        { value: 5, label: '5' },
                                    ]}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(value) => t('PROFILE.CURSOR_SPEED_LABEL', { value })}
                                />
                            </Box>
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
                            disabled={isSubmitting}
                            fullWidth
                            sx={{ mt: 2 }}
                        >
                            {t('PROFILE.SAVE_SETTINGS')}
                        </Button>
                    </Form>
                )}
            </Formik>
        </Box>
    );
}
